import React, {
    createContext,
    useMemo,
    useState,
    useCallback,
    useEffect
} from "react";

import {
    FUNCTIONALITY_COOKIE_KEY,
    ANALYTICAL_COOKIE_KEY,
    ADVERTISING_COOKIE_KEY
} from "core/localStorageKeys";
import { CONSENT_COOKIE_NAME } from "core/cookieKeys";

const CookieConsentContext = createContext({
    hasConsentSettings: true, // hide banner from jest snapshots
    cookies: {
        functionality: true,
        analytics: true,
        advertising: true
    },
    saveConsentSettings() {},
    acceptAllCookies() {}
});

export const CookieConsentProvider = ({ ssrConsentSettings, children }) => {
    // If cookie values change, update `check` value to break cache & look up new values.
    const [check, setCheck] = useState(null);

    const saveConsentSettings = useCallback(cookieTypes => {
        const { functionality, analytics, advertising } = cookieTypes;
        setConsentCookie({ functionality, analytics, advertising });
        setCheck(Date.now());
    }, []);

    const acceptAllCookies = useCallback(() => {
        saveConsentSettings({
            functionality: true,
            analytics: true,
            advertising: true
        });
    }, []);

    const value = useMemo(() => {
        const { cookies, hasConsentSettings } = getConsentCookie(
            ssrConsentSettings
        );
        return {
            // values
            cookies,
            hasConsentSettings,
            // methods
            saveConsentSettings,
            acceptAllCookies
        };
    }, [check]);

    ///////////////////////
    // TODO: Temporary code. Remove later.
    //
    // NOTE:
    // The initial launch used local-storage instead of cookies to determine
    // whether or not to show the consent banner.
    // If users have consent settings in local-storage, we need to migrate
    // their settings to a cookie. During migration, users might see the consent
    // banner flicker - because the server HTML included the banner, and this
    // `useEffect` later removed the banner (after migrating their settings).
    useEffect(() => {
        if (value.hasConsentSettings === false) {
            const migrateSettings = getMigrationSettings();
            if (migrateSettings) {
                setConsentCookie(migrateSettings);
                setCheck(Date.now());
            }
        }
    }, []);
    ///////////////////////

    return (
        <CookieConsentContext.Provider value={value}>
            {children}
        </CookieConsentContext.Provider>
    );
};

export default CookieConsentContext;

// Save consent settings to cookie so that both server & client
// HTML match - showing / hiding the consent banner.
const setConsentCookie = ({ functionality, analytics, advertising }) => {
    const dateOfConsent = Date.now();
    const expireDate = new Date(dateOfConsent); // easier to mock in jest this way
    expireDate.setFullYear(expireDate.getFullYear() + 1);
    const expires = expireDate.toUTCString();
    const value = encodeURIComponent(
        JSON.stringify({
            dateOfConsent,
            functionality: !!functionality, // force to be a boolean
            analytics: !!analytics,
            advertising: !!advertising
        })
    );
    document.cookie = `${CONSENT_COOKIE_NAME}=${value};expires=${expires};path=/`;
};

// Determine if consent has been set.
// NOTE:
// - `ssrConsentSettings` is for when server-side is generating HTML
//   and does not have access to `document.cookie`.
// - The purpose of storing cookie_consent in a cookie, and not in
//   local-storage, is so that the client & server have access to
//   the consent settings. We want the server HTML to include the
//   consent banner for performance / SEO. But the server & client
//   HTML need to match during hydration. Thus, we use cookies.
const getConsentCookie = ssrConsentSettings => {
    const defaultSettings = {
        hasConsentSettings: false,
        cookies: {
            functionality: true,
            analytics: true,
            advertising: true
        }
    };

    try {
        if (typeof document === "undefined") {
            if (ssrConsentSettings) {
                return {
                    hasConsentSettings: true,
                    cookies: ssrConsentSettings
                };
            }
            return defaultSettings;
        }

        const cookies = document.cookie.split(/;\s*/);
        const consentCookie = cookies.find(
            cookie => cookie.indexOf(CONSENT_COOKIE_NAME + "=") === 0
        );
        if (consentCookie) {
            const value = consentCookie.split("=")[1];
            return {
                hasConsentSettings: true,
                cookies: JSON.parse(decodeURIComponent(value))
            };
        }

        return defaultSettings;
    } catch (e) {
        // ignore JSON errors
        return defaultSettings;
    }
};

// TODO: delete this function when migration code is removed.
const getMigrationSettings = () => {
    const functionality = getMigrationValue(FUNCTIONALITY_COOKIE_KEY);
    const analytics = getMigrationValue(ANALYTICAL_COOKIE_KEY);
    const advertising = getMigrationValue(ADVERTISING_COOKIE_KEY);
    if (functionality === null || analytics === null || advertising === null) {
        return null;
    }
    return {
        functionality,
        analytics,
        advertising
    };
};

// TODO: delete this function when migration code is removed.
const getMigrationValue = key => {
    try {
        const json = localStorage.getItem(key);
        if (!json) {
            return null;
        }
        localStorage.removeItem(key);
        const data = JSON.parse(json);
        if (data && typeof data.value === "boolean") {
            return data.value;
        }
    } catch (e) {
        // ignore JSON errors
    }
    return null;
};
